import { addDays, startOfDay, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { ITaggedNoteListItem } from './models/ITaggedNote.interface';

export type NoteDateGroupKey =
  | 'favourites' | 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'older';

export interface NoteDateGroup {
  key: NoteDateGroupKey;
  label: string;
  notes: ITaggedNoteListItem[];
  unread: number;
}

const LABELS: Record<NoteDateGroupKey, string> = {
  favourites: 'Favourites',
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'Earlier this week',
  lastWeek: 'Last week',
  thisMonth: 'Earlier this month',
  lastMonth: 'Last month',
  older: 'Older',
};

const ORDER: NoteDateGroupKey[] = ['favourites', 'today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'older'];

/**
 * The notes list in Outlook-style sections. Starred notes are pinned in Favourites at the top; the
 * rest fall by last activity, which is the order the list already comes in, so a reply moves a note
 * up to Today. Weeks start on Monday. Empty sections are left out.
 *
 * Boundaries are the browser's clock. The first matching section wins, so at the start of a month
 * "Last week" takes the days it shares with "Last month".
 */
export function groupNotesByDate(notes: ITaggedNoteListItem[], now = new Date()): NoteDateGroup[] {
  const today = startOfDay(now).getTime();
  const yesterday = addDays(today, -1).getTime();
  const thisWeek = startOfWeek(now, { weekStartsOn: 1 }).getTime();
  const lastWeek = addDays(thisWeek, -7).getTime();
  const thisMonth = startOfMonth(now).getTime();
  const lastMonth = startOfMonth(subMonths(now, 1)).getTime();

  const keyOf = (note: ITaggedNoteListItem): NoteDateGroupKey => {
    if (note.isFavorite) return 'favourites';
    const time = new Date(note.lastActivityDate).getTime();
    // An unparsable date goes to the bottom rather than pretending to be recent.
    if (Number.isNaN(time)) return 'older';
    if (time >= today) return 'today';
    if (time >= yesterday) return 'yesterday';
    if (time >= thisWeek) return 'thisWeek';
    if (time >= lastWeek) return 'lastWeek';
    if (time >= thisMonth) return 'thisMonth';
    if (time >= lastMonth) return 'lastMonth';
    return 'older';
  };

  const byKey = new Map<NoteDateGroupKey, NoteDateGroup>();
  for (const note of notes) {
    const key = keyOf(note);
    let group = byKey.get(key);
    if (!group) byKey.set(key, group = { key, label: LABELS[key], notes: [], unread: 0 });
    group.notes.push(note);
    if ((note.unreadCount || 0) > 0) group.unread++;
  }
  return ORDER.filter(key => byKey.has(key)).map(key => byKey.get(key)!);
}

/**
 * The sections a person has minimised. Remembered per browser and shared by the Notes page and the
 * slide-in panel, so folding "Older" in one folds it in the other.
 */
export class NoteGroupCollapseState {
  private static readonly KEY = 'notes.collapsedGroups';
  private collapsed = new Set<string>(NoteGroupCollapseState.read());

  isCollapsed(key: string): boolean { return this.collapsed.has(key); }

  toggle(key: string): void {
    // Re-read first: the other list may have changed it since this one loaded.
    this.collapsed = new Set(NoteGroupCollapseState.read());
    if (!this.collapsed.delete(key)) this.collapsed.add(key);
    try { localStorage.setItem(NoteGroupCollapseState.KEY, JSON.stringify([...this.collapsed])); } catch {}
  }

  private static read(): string[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(NoteGroupCollapseState.KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter(k => typeof k === 'string') : [];
    } catch { return []; }
  }
}
